/** Карточка артикула (боковая панель, `card=article:<артикул>`; у WB артикул — vendor_code):
 *  шапка товара → KPI периода против сравнения → график → P&L артикула → география (Ozon —
 *  кластеры доставки, WB — федеральные округа, `mp.ts`) → поставки и кампании.
 *
 *  Все суммы — только по фактам артикула: `/summary` и `/trend` со `scope=article:<артикул>`,
 *  разбивки — `POST /tree` с `path=[article]`. Фильтры страницы (период, сравнение, юрлицо,
 *  общие расходы) — как есть. Каждый блок грузится сам: свой скелетон, своя ошибка текстом. */
import React, { useMemo, useState } from "react";

import type { Grain, OzFilters, TreeRequest, TreeRow } from "@/api/ozonAnalytics";
import { cn } from "@/lib/utils";
import { share, shown } from "./calc";
import { Block, Empty, More, Skeleton } from "./cardBlocks";
import { cogsNullTitle } from "./cogs";
import { pct, rub } from "./format";
import { defaultGrain } from "./itemTrend";
import { avgPriceKpi, buildKpis } from "./kpi";
import { useMp, type GeoBlock } from "./mp";
import { KpiGrid, PnlTable } from "./pnl";
import { ProductLink, ProductPhoto } from "./ProductCard";
import { useProduct, useSummary, useTreePage } from "./queries";
import { ErrorBox } from "./shared";
import type { CardRef } from "./state";
import { TrendChart } from "./TrendChart";
import { tdNum, thClass } from "./ui";

/** Строк в блоках «география», «поставки», «кампании». */
const TOP = 10;
/** География запрашивается вся (кластеров/округов десятки): строку «без значения» нужно найти,
 *  где бы она ни была по величине, и поставить последней вне топа. */
const GEO_LIMIT = 200;

export function ArticleCard({
  offerId,
  filters,
  warning,
  titleId,
  onOpenCard,
}: {
  offerId: string;
  filters: OzFilters;
  warning: string | null;
  /** id заголовка — на него ссылается aria-labelledby панели */
  titleId: string;
  /** переход к другой карточке внутри панели (поставка артикула) */
  onOpenCard: (card: CardRef) => void;
}): React.ReactElement {
  const mp = useMp();
  const product = useProduct(mp.id, offerId, filters.legal);
  // артикула нет в справочнике (404) — только шапка с ошибкой: блоки показали бы «честные нули»
  // несуществующего артикула. Пока /product грузится, блоки грузятся параллельно.
  const notFound =
    (product.error as { response?: { status?: number } } | null)?.response?.status === 404;
  return (
    <div className="space-y-5">
      <Header q={product} offerId={offerId} titleId={titleId} />
      {!notFound && (
        <ArticleBlocks
          offerId={offerId}
          filters={filters}
          warning={warning}
          onOpenCard={onOpenCard}
        />
      )}
    </div>
  );
}

function ArticleBlocks({
  offerId,
  filters,
  warning,
  onOpenCard,
}: {
  offerId: string;
  filters: OzFilters;
  warning: string | null;
  onOpenCard: (card: CardRef) => void;
}): React.ReactElement {
  const mp = useMp();
  const scope = useMemo(() => [`article:${offerId}`], [offerId]);
  const summary = useSummary(mp.id, filters, scope);
  const [grain, setGrain] = useState<Grain>(() =>
    defaultGrain({ from: filters.date_from, to: filters.date_to }),
  );

  return (
    <div className="space-y-5">

      <Block title="Показатели периода">
        {summary.isLoading && <Skeleton rows={2} />}
        {summary.isError && <ErrorBox error={summary.error} what="показатели артикула" />}
        {summary.data && (
          <KpiGrid
            kpis={[...buildKpis(summary.data, mp), avgPriceKpi(summary.data)]}
            warning={warning}
            compact
          />
        )}
      </Block>

      <TrendChart
        filters={filters}
        grain={grain}
        onGrain={setGrain}
        scope={scope}
        title="Выручка и расходы артикула"
      />

      <Block title="P&L артикула">
        {summary.isLoading && <Skeleton rows={4} />}
        {summary.isError && <ErrorBox error={summary.error} what="P&L артикула" />}
        {summary.data && (
          <PnlTable
            summary={summary.data}
            filters={filters}
            warning={warning}
            charts={false}
            compact
          />
        )}
      </Block>

      <GeoBlockView offerId={offerId} filters={filters} geo={mp.geo} />
      {/* списки поставок и кампаний есть у обеих площадок (/tree с obj_kind); переход в карточку —
          только если такая карточка у площадки есть */}
      <ObjectsBlock
        offerId={offerId}
        filters={filters}
        kind="supply"
        onOpenCard={mp.cards.includes("supply") ? onOpenCard : undefined}
      />
      <ObjectsBlock
        offerId={offerId}
        filters={filters}
        kind="campaign"
        onOpenCard={mp.cards.includes("campaign") ? onOpenCard : undefined}
      />
    </div>
  );
}

function Header({
  q,
  offerId,
  titleId,
}: {
  q: ReturnType<typeof useProduct>;
  offerId: string;
  titleId: string;
}): React.ReactElement {
  const mp = useMp();
  const p = q.data?.product ?? null;
  const name = p ? (p.name_1c ?? p.name_mp) : null;
  return (
    <div className="space-y-2" data-testid="article-header">
      <div className="flex items-start gap-3">
        {p ? (
          <ProductPhoto key={p.image_url ?? ""} url={p.image_url} alt={name ?? offerId} small />
        ) : (
          q.isLoading && <div className="size-16 shrink-0 animate-pulse rounded bg-muted" />
        )}
        <div className="min-w-0 space-y-0.5">
          <div className="flex items-baseline">
            <h2 id={titleId} className="text-xl font-semibold leading-tight">
              {offerId}
            </h2>
            {p && <ProductLink mpSku={p.mp_sku} />}
          </div>
          {q.isLoading && <div className="h-4 w-64 animate-pulse rounded bg-muted" />}
          {p && (
            <>
              <div className="text-sm">{name ?? "без названия"}</div>
              {p.name_1c !== null && p.name_mp !== null && (
                <div className="text-xs text-muted-foreground">
                  {mp.label}: {p.name_mp}
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                {p.sku_count} {mp.skuLabel}
                {p.code_1c && ` · код 1С ${p.code_1c}`}
                {p.name_1c === null && " · 1С: не сопоставлено"}
              </div>
            </>
          )}
        </div>
      </div>
      {q.isError && <ErrorBox error={q.error} what="карточку товара" />}
    </div>
  );
}

/** Запрос страницы дерева внутри артикула: следующий уровень `level`, топ по `sort`.
 *  Сравнение блокам не нужно — не считаем. */
function articleTreeReq(
  offerId: string,
  filters: OzFilters,
  level: string,
  sort: TreeRequest["sort"],
  objKind: string | null = null,
  limit: number = TOP,
): TreeRequest {
  return {
    ...filters,
    cmp_from: null,
    cmp_to: null,
    levels: ["article", level],
    path: [{ dim: "article", key: offerId }],
    search: null,
    limit,
    offset: 0,
    sort,
    obj_kind: objKind,
  };
}

/** Топ по величине без строки «без значения» (ключ '' — NULL) + она сама отдельно: в ней
 *  расходы без своей выручки (маржа −2000%), её место — последней, вне топа. */
function splitGeo(rows: TreeRow[], top: number): { top: TreeRow[]; none: TreeRow | null } {
  const none = rows.find((r) => r.key === "") ?? null;
  return { top: rows.filter((r) => r.key !== "").slice(0, top), none };
}

/** География продаж артикула — куда ушёл заказ: у Ozon кластер ДОСТАВКИ (`cluster_to`), у WB —
 *  федеральный округ. Хранение, поставки, реклама географии не имеют — они в строке «без». */
function GeoBlockView({
  offerId,
  filters,
  geo,
}: {
  offerId: string;
  filters: OzFilters;
  geo: GeoBlock;
}): React.ReactElement {
  const mp = useMp();
  const q = useTreePage(mp.id, articleTreeReq(offerId, filters, geo.dim, "abs", null, GEO_LIMIT));
  const split = q.data ? splitGeo(q.data.rows, TOP) : null;
  // расходы и маржа — с себестоимостью; без неё (период раньше её начала) — «—» с причиной
  const why = cogsNullTitle(q.data?.cogs, mp.label, "cur");
  const exp = (r: TreeRow): string =>
    r.cur.expenses == null ? "—" : rub(shown(r.cur.expenses, "expense"));
  const marginPctText = (r: TreeRow): string =>
    r.cur.net_margin == null ? "—" : pct(share(r.cur.net_margin, r.cur.rev));
  const nullTitle = (v: number | null | undefined): string | undefined =>
    v == null ? why : undefined;
  return (
    <Block title={geo.title}>
      {q.isLoading && <Skeleton rows={3} />}
      {q.isError && <ErrorBox error={q.error} what={geo.what} />}
      {q.data && q.data.rows.length === 0 && <Empty />}
      {q.data && q.data.rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border bg-card">
          <table className="w-full text-xs" data-testid="clusters-table">
            <thead className="bg-muted/40">
              <tr>
                <th className={cn(thClass, "text-left")}>{geo.col}</th>
                <th className={thClass}>Выручка</th>
                <th className={thClass} title={`все удержания ${mp.label} + себестоимость`}>
                  Расходы
                </th>
                <th className={thClass}>Маржа %</th>
              </tr>
            </thead>
            <tbody>
              {split!.top.map((r) => (
                <tr key={r.key} className="border-t">
                  <td className="px-2 py-1">{r.label}</td>
                  <td className={tdNum}>{rub(r.cur.rev)}</td>
                  <td className={tdNum} title={nullTitle(r.cur.expenses)}>{exp(r)}</td>
                  <td className={tdNum} title={nullTitle(r.cur.net_margin)}>
                    {marginPctText(r)}
                  </td>
                </tr>
              ))}
              {split!.none && (
                <tr className="border-t text-muted-foreground" data-testid="no-cluster-row">
                  <td className="px-2 py-1">{geo.noneLabel}</td>
                  <td className={tdNum}>{rub(split!.none.cur.rev)}</td>
                  <td className={tdNum} title={nullTitle(split!.none.cur.expenses)}>
                    {exp(split!.none)}
                  </td>
                  <td className={tdNum} title={geo.noneMarginTitle}>
                    —
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <More
            data={q.data}
            noun={geo.noun}
            shown={split!.top.length + (split!.none ? 1 : 0)}
          />
        </div>
      )}
    </Block>
  );
}

const OBJ_BLOCKS = {
  supply: {
    title: "Поставки",
    col: "Поставка",
    sumCol: "Начисления",
    what: "поставки артикула",
    noun: "поставок",
  },
  campaign: {
    title: "Рекламные кампании",
    col: "Кампания",
    sumCol: "Расход",
    what: "рекламные кампании артикула",
    noun: "кампаний",
  },
} as const;

/** Поставки / кампании артикула: узлы `obj` нужного вида (бэкенд фильтрует `obj_kind` — иначе
 *  топ забили бы отправления), самые дорогие первыми. Сумма — расходом, без минуса. */
function ObjectsBlock({
  offerId,
  filters,
  kind,
  onOpenCard,
}: {
  offerId: string;
  filters: OzFilters;
  kind: keyof typeof OBJ_BLOCKS;
  /** номер поставки / кампании — ссылка на её карточку (переход внутри панели) */
  onOpenCard?: (card: CardRef) => void;
}): React.ReactElement {
  const mp = useMp();
  const b = OBJ_BLOCKS[kind];
  const q = useTreePage(mp.id, articleTreeReq(offerId, filters, "obj", "cost", kind));
  const amount = (r: TreeRow): number => shown(r.cur.rev + r.cur.cost, "expense");
  return (
    <Block title={b.title}>
      {q.isLoading && <Skeleton rows={2} />}
      {q.isError && <ErrorBox error={q.error} what={b.what} />}
      {q.data && q.data.rows.length === 0 && <Empty />}
      {q.data && q.data.rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border bg-card">
          <table className="w-full text-xs" data-testid={`${kind}-table`}>
            <thead className="bg-muted/40">
              <tr>
                <th className={cn(thClass, "text-left")}>{b.col}</th>
                <th className={thClass}>{b.sumCol}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((r) => (
                <tr key={r.key} className="border-t">
                  <td className="px-2 py-1">
                    <ObjLabel row={r} kind={kind} onOpenCard={onOpenCard} />
                  </td>
                  <td className={tdNum}>{rub(amount(r))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <More data={q.data} noun={b.noun} />
        </div>
      )}
    </Block>
  );
}

/** Номер объекта; с номером и `onOpenCard` — кнопка-ссылка на карточку поставки / кампании
 *  (ключ узла `<вид>:<номер>`, номер = ключ карточки; у кампании — только цифры, как в адресе). */
function ObjLabel({
  row,
  kind,
  onOpenCard,
}: {
  row: TreeRow;
  kind: keyof typeof OBJ_BLOCKS;
  onOpenCard?: (card: CardRef) => void;
}): React.ReactElement {
  const prefix = `${kind}:`;
  const number = row.key.startsWith(prefix) ? row.key.slice(prefix.length) : "";
  const valid = kind === "campaign" ? /^[0-9]+$/.test(number) : number !== "";
  if (!onOpenCard || !valid) return <>{row.label}</>;
  return (
    <button
      type="button"
      className="tabular-nums text-primary hover:underline"
      title={kind === "supply" ? "Открыть карточку поставки" : "Открыть карточку кампании"}
      onClick={() => onOpenCard({ kind, key: number })}
    >
      {row.label}
    </button>
  );
}
